import React, { useState, useEffect, useMemo } from "react";
import { TabbedCard, LoadingV2 } from "@ucl/library";
import { FinancialProductCarousel } from "./FinancialProductCarousel";
import { FinancialProduct } from "./FinancialProductCard";
import { useDepositApplicationSelections } from "@/hooks/useDepositApplicationSelections";
import { useFinancialProducts, SegmentProductCombination } from "@hooks/useFinancialProducts";
import { FinancialProductReadDTO } from "@/types/FinancialApplication";
import FinancialApplicationApiClient from "@/api/financialApplicationApiClient";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import CheckingIconV2 from "@/assets/select-product-screen/checking-icon.svg?react";
import MoneyMarketIconV2 from "@/assets/select-product-screen/money-market-icon.svg?react";
import TrustAccountsIconV2 from "@/assets/select-product-screen/trust-accounts-icon.svg?react";
import CertificateOfDepositIconV2 from "@/assets/select-product-screen/certificate-of-deposit-icon.svg?react";
import "./SelectProductForm.scss";

// Icon types for different product types
type IconType = 'checking' | 'moneyMarket' | 'trustAccounts' | 'certificateOfDeposit';

// Convert API product to UI product format
const convertApiProductToUiProduct = (apiProduct: FinancialProductReadDTO): FinancialProduct => ({
  id: apiProduct.productSlug,
  title: apiProduct.productName,
  description: apiProduct.productDescription,
  features: apiProduct.productFeatures,
  customerSegment: apiProduct.customerSegment,
});

// Get icon type for product type
const getIconTypeForProductType = (productType: string): IconType => {
  const lowerType = productType.toLowerCase();
  if (lowerType.includes("checking")) return 'checking';
  if (lowerType.includes("money market")) return 'moneyMarket';
  if (lowerType.includes("trust")) return 'trustAccounts';
  if (lowerType.includes("certificate")) return 'certificateOfDeposit';
  return 'checking'; // default
};

// Render icon based on icon type
const renderIcon = (iconType: IconType) => {
  switch (iconType) {
    case 'checking':
      return <CheckingIconV2 />;
    case 'moneyMarket':
      return <MoneyMarketIconV2 />;
    case 'trustAccounts':
      return <TrustAccountsIconV2 />;
    case 'certificateOfDeposit':
      return <CertificateOfDepositIconV2 />;
    default:
      return <CheckingIconV2 />;
  }
};

export interface SelectProductFormProps {
  apiClient: FinancialApplicationApiClient;
  financialApplicationId: string;
}

export const SelectProductForm: React.FC<SelectProductFormProps> = ({
  apiClient,
  financialApplicationId,
}) => {
  const {
    products,
    customerSegments,
    segmentProductCombinations,
    loading: productsLoading,
    error: productsError,
  } = useFinancialProducts(apiClient);

  const {
    selectedProductsByTabKey,
    setSelectedProductsByTabKey,
  } = useDepositApplicationSelections(financialApplicationId, apiClient, products);

  // Set default selectedCustomerSegment to first available segment
  const [selectedCustomerSegment, setSelectedCustomerSegment] = useState<string>(
    customerSegments.at(0) ?? ""
  );

  const [activeTabKey, setActiveTabKey] = useState<string>("");

  // Update selectedCustomerSegment when customer segments change
  useEffect(() => {
    if (customerSegments.length > 0 && !customerSegments.includes(selectedCustomerSegment)) {
      setSelectedCustomerSegment(customerSegments.at(0) ?? "");
    }
  }, [customerSegments, selectedCustomerSegment]);

  const handleCustomerSegmentChange = (newSegment: string) => {
    setSelectedCustomerSegment(newSegment);
    // activeTabKey will be reset by the useEffect when visibleTabs change
  };

  const handleTabChange = (tabKey: string) => {
    setActiveTabKey(tabKey);
  };

  const handleProductSelect = (tabKey: string, productIds: string[]) => {
    setSelectedProductsByTabKey({
      ...selectedProductsByTabKey,
      [tabKey]: productIds,
    });
  };

  const getSelectedCount = (tabKey: string): number => {
    return selectedProductsByTabKey[tabKey]?.length ?? 0;
  };

  const renderTabHeaderWithIndicator = (icon: React.ReactElement, title: string, tabKey: string) => {
    const count = getSelectedCount(tabKey);
    return (
      <>
        {icon} {title}
        {count > 0 && <span className="tab-indicator">
          {count} <FontAwesomeIcon icon={faCheck} />
          </span>}
      </>
    );
  };

  // Get products for a specific customer segment and product type
  const getProductsForCombination = (
    combination: SegmentProductCombination
  ): FinancialProduct[] => {
    return products
      .filter(
        (p) =>
          p.customerSegment === combination.customerSegment &&
          p.productType === combination.productType
      )
      .map(convertApiProductToUiProduct);
  };

  // Generate tab data dynamically based on available combinations
  const allTabData = useMemo(() => {
    return segmentProductCombinations.map((combination) => {
      const iconType = getIconTypeForProductType(combination.productType);
      const icon = renderIcon(iconType);
      const productsForThisCombination = getProductsForCombination(combination);

      return {
        tabKey: combination.tabKey,
        headerContent: renderTabHeaderWithIndicator(
          icon,
          combination.displayName,
          combination.tabKey
        ),
        tabContent: (
          <FinancialProductCarousel
            products={productsForThisCombination}
            selectedProducts={selectedProductsByTabKey[combination.tabKey] ?? []}
            onProductSelect={(productIds) => handleProductSelect(combination.tabKey, productIds)}
            multiSelect
          />
        ),
      };
    });
  }, [segmentProductCombinations, products, selectedProductsByTabKey]);

  // Filter tabs based on current selectedCustomerSegment
  const visibleTabs = allTabData.filter((tab) => {
    const combination = segmentProductCombinations.find((c) => c.tabKey === tab.tabKey);
    return combination?.customerSegment === selectedCustomerSegment;
  });

  // Set default activeTabKey when visibleTabs change or when not set
  useEffect(() => {
    if (
      visibleTabs.length > 0 &&
      (!activeTabKey || !visibleTabs.find((tab) => tab.tabKey === activeTabKey))
    ) {
      setActiveTabKey(visibleTabs[0].tabKey);
    }
  }, [visibleTabs, activeTabKey]);

  const loading = productsLoading;

  if (loading) {
    return (
      <section className="select-product-form">
        <LoadingV2 />
      </section>
    );
  }

  if (productsError) {
    return (
      <section className="select-product-form">
        <div className="error-message">Error loading financial products: {productsError}</div>
      </section>
    );
  }

  return (
    <section className="select-product-form">
      <div className="select-product-form__classification">
        <div className="classification-selector">
          {customerSegments.map((segment) => (
            <button
              key={segment}
              className={`classification-button ${selectedCustomerSegment === segment ? "active" : ""}`}
              onClick={() => handleCustomerSegmentChange(segment)}
            >
              {segment} Products
            </button>
          ))}
        </div>
      </div>
      {visibleTabs.length > 0 && (
        <TabbedCard
          key={selectedCustomerSegment}
          tabs={visibleTabs}
          activeTabKey={activeTabKey}
          onTabChange={handleTabChange}
          className="select-product-form__tabbed-card"
        />
      )}
    </section>
  );
};

export default SelectProductForm;
